import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
// Common infant formula brands and manufacturers
const COMMON_BRANDS = [
    'Abbott',
    'Similac',
    'Enfamil',
    'Mead Johnson',
    'Gerber',
    'Nestlé',
    'Good Start',
    "Parent's Choice",
    "Earth's Best",
    'Happy Baby',
    'Organic',
    'Kirkland',
    "Member's Mark",
    'Up & Up',
    'Bobbie',
    'HiPP',
    'Holle',
    'Kendamil',
    'Plum Organics',
    'Honest Company',
    'Burt\'s Bees Baby',
    'Perrigo',
    'PBM Nutritionals',
    'Store Brand',
    'Generic',
].sort();
export default function SearchForm({ onSearch, loading }) {
    const [brand, setBrand] = useState('');
    const [code, setCode] = useState('');
    const [productKeyword, setProductKeyword] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredBrands, setFilteredBrands] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const brandInputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const [autocompleteSuggestion, setAutocompleteSuggestion] = useState('');
    // Filter brands based on input
    useEffect(() => {
        if (brand.trim().length > 0) {
            const filtered = COMMON_BRANDS.filter((b) => b.toLowerCase().includes(brand.toLowerCase()));
            // Check if there's an exact match (case-insensitive, ignoring whitespace)
            const hasExactMatch = filtered.some((b) => b.toLowerCase().trim() === brand.toLowerCase().trim());
            // Find single match that starts with input for autocomplete
            const startsWithMatches = filtered.filter((b) => b.toLowerCase().startsWith(brand.toLowerCase()));
            if (startsWithMatches.length === 1 && !hasExactMatch) {
                setAutocompleteSuggestion(startsWithMatches[0] ?? '');
            }
            else {
                setAutocompleteSuggestion('');
            }
            setFilteredBrands(filtered);
            setShowSuggestions(filtered.length > 0 && !hasExactMatch);
        }
        else {
            setFilteredBrands([]);
            setShowSuggestions(false);
            setAutocompleteSuggestion('');
        }
        setSelectedIndex(-1);
    }, [brand]);
    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (suggestionsRef.current &&
                !suggestionsRef.current.contains(e.target) &&
                brandInputRef.current &&
                !brandInputRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    // Load initial values from URL params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const brandParam = params.get('brand');
        const codeParam = params.get('code');
        const qParam = params.get('q');
        if (brandParam)
            setBrand(brandParam);
        if (codeParam)
            setCode(codeParam);
        if (qParam)
            setProductKeyword(qParam);
    }, []);
    const handleSubmit = (e) => {
        e.preventDefault();
        const hasBrand = brand.trim().length > 0;
        const hasCode = code.trim().length > 0;
        const hasKeyword = productKeyword.trim().length > 0;
        if (hasBrand || hasCode || hasKeyword) {
            setShowSuggestions(false);
            onSearch(brand.trim(), code.trim(), productKeyword.trim());
        }
    };
    const handleBrandSelect = (selectedBrand) => {
        setBrand(selectedBrand);
        setShowSuggestions(false);
        brandInputRef.current?.focus();
    };
    const handleBrandKeyDown = (e) => {
        // Handle Tab for autocomplete
        if (e.key === 'Tab' && autocompleteSuggestion) {
            e.preventDefault();
            setBrand(autocompleteSuggestion);
            setAutocompleteSuggestion('');
            return;
        }
        if (!showSuggestions || filteredBrands.length === 0)
            return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev < filteredBrands.length - 1 ? prev + 1 : prev));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        }
        else if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            const selected = filteredBrands[selectedIndex];
            if (selected)
                handleBrandSelect(selected);
        }
        else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };
    const highlightMatch = (text, query) => {
        if (!query)
            return text;
        const index = text.toLowerCase().indexOf(query.toLowerCase());
        if (index === -1)
            return text;
        return (_jsxs(_Fragment, { children: [text.substring(0, index), _jsx("strong", { children: text.substring(index, index + query.length) }), text.substring(index + query.length)] }));
    };
    return (_jsxs("div", { className: "bottle-wrapper", children: [_jsxs("div", { className: "bottle-nipple", children: [_jsx("div", { className: "nipple-tip" }), _jsx("div", { className: "nipple-mid" }), _jsx("div", { className: "nipple-base" })] }), _jsx("div", { className: "bottle-collar" }), _jsxs("div", { className: "bottle-body", children: [_jsx("div", { className: "bottle-highlight bottle-highlight-left" }), _jsx("div", { className: "bottle-highlight bottle-highlight-right" }), _jsx("div", { className: "bottle-milk", children: _jsxs("div", { className: "milk-foam", children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {})] }) }), _jsxs("form", { className: "search-form", onSubmit: handleSubmit, children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "brand", children: "Brand / Manufacturer" }), _jsxs("div", { style: { position: 'relative' }, children: [_jsxs("div", { style: { position: 'relative' }, children: [autocompleteSuggestion && (_jsxs("div", { style: {
                                                            position: 'absolute',
                                                            top: '2px',
                                                            left: '2px',
                                                            right: '2px',
                                                            bottom: '2px',
                                                            pointerEvents: 'none',
                                                            // Match input padding exactly: var(--spacing-sm) var(--spacing-md) = 0.5rem 1rem
                                                            padding: '0.5rem 1rem',
                                                            color: 'transparent',
                                                            whiteSpace: 'pre',
                                                            overflow: 'hidden',
                                                            font: 'inherit',
                                                            fontSize: '0.95rem',
                                                            lineHeight: '1.5',
                                                            letterSpacing: 'normal',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }, children: [_jsx("span", { style: { visibility: 'hidden' }, children: brand }), _jsx("span", { style: { color: '#9ca3af', visibility: 'visible' }, children: autocompleteSuggestion.substring(brand.length) })] })), _jsx("input", { type: "text", id: "brand", ref: brandInputRef, value: brand, onChange: (e) => setBrand(e.target.value), onKeyDown: handleBrandKeyDown, onFocus: () => {
                                                            if (brand.trim() && filteredBrands.length > 0) {
                                                                // Don't show suggestions if there's an exact match
                                                                const hasExactMatch = filteredBrands.some((b) => b.toLowerCase().trim() === brand.toLowerCase().trim());
                                                                setShowSuggestions(!hasExactMatch);
                                                            }
                                                        }, placeholder: "e.g., Abbott, Similac, Enfamil", disabled: loading, autoComplete: "off", style: { position: 'relative', backgroundColor: 'transparent' } })] }), showSuggestions && filteredBrands.length > 0 && (_jsx("div", { ref: suggestionsRef, className: "autocomplete-dropdown", style: {
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    borderRadius: '0.375rem',
                                                    marginTop: '0.25rem',
                                                    maxHeight: '200px',
                                                    overflowY: 'auto',
                                                    zIndex: 10,
                                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                }, children: filteredBrands.map((brandName, index) => (_jsx("div", { onClick: () => handleBrandSelect(brandName), className: `autocomplete-item ${index === selectedIndex ? 'selected' : ''}`, style: {
                                                        padding: '0.5rem 0.75rem',
                                                        cursor: 'pointer',
                                                        borderBottom: index < filteredBrands.length - 1 ? '1px solid var(--border-color, #e5e7eb)' : 'none',
                                                    }, onMouseEnter: () => setSelectedIndex(index), children: highlightMatch(brandName, brand) }, brandName))) }))] }), _jsx("small", { children: "Enter the brand name or manufacturer (optional if you provide a code or keyword)" })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "code", children: "Lot Code / Batch Number (Optional)" }), _jsx("input", { type: "text", id: "code", value: code, onChange: (e) => setCode(e.target.value), placeholder: "e.g., LOT 12345, BATCH-ABC-123", disabled: loading }), _jsx("small", { children: "Enter the lot code or batch number if available. This will improve match accuracy." })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "productKeyword", children: "Product Keyword (Optional)" }), _jsx("input", { type: "text", id: "productKeyword", value: productKeyword, onChange: (e) => setProductKeyword(e.target.value), placeholder: "e.g., Pro Advance, Gentlease, Infant", disabled: loading }), _jsx("small", { children: "Filter results by specific product name or keyword" })] }), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: loading || (!brand.trim() && !code.trim() && !productKeyword.trim()), children: loading ? 'Searching...' : 'Search Recalls' })] })] }), _jsx("div", { className: "bottle-shadow" })] }));
}
